// ============================================================
// TMR Portal Admin — uma regra de negócio (rota /regras-negocio/:ruleKey): o padrão e as exceções (25)
// ============================================================
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Notice } from '../../components/Notice';
import { RecordPicker } from '../../components/RecordPicker';
import { PageHeader, Table, Td } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { maskCNPJ } from '../../lib/format';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useCompanySearch } from '../../data/companies/queries';
import type { CompanyListItem } from '../../data/companies/contract';
import {
  useBusinessRules,
  useRemoveRuleOverride,
  useRuleOverrides,
  useSetRuleOverride,
  useUpdateRuleDefault,
} from '../../data/businessRules/queries';
import {
  defaultOriginLabel,
  overrideCountLabel,
  overrideIneligibility,
  overrideOriginLabel,
  ruleRangeLabel,
  ruleReach,
  ruleValueLabel,
} from '../../data/businessRules/status';
import type { BusinessRule, RuleOverride, RuleValue } from '../../data/businessRules/contract';
import { RuleValueModal } from './RuleValueModal';

/** The Empresa whose own value is being set from this page, with what the dialog needs to know about it. */
interface OverrideTarget {
  companyId: string;
  companyName: string;
  /** Whether it has a `produtores.id` — without one the server refuses, and the dialog says why first. */
  legacyLinked: boolean;
  /** Its own value today; null when it follows the default. */
  current: RuleValue | null;
}

/**
 * One business rule: the TMR's default and every Empresa that does not follow it (`25` §3.4).
 *
 * For the sales module this is where the operator answers "which clinics are on the Vendas?" — the default
 * is "Legado", so the Empresas listed here are the clinics released — and releases the next one, from a
 * search, without opening each Empresa. The same list serves any rule: it is the Empresas with an exception.
 *
 * The page opens with `business_rules.global.read` (the route's gate), like the list of rules. The Empresas
 * are a separate read with `company.business_rules.read`, scoped to the operator's reach, so an operator with
 * the first key only sees the default and is told why the list is missing.
 */
export function BusinessRuleDetailPage() {
  const { ruleKey = '' } = useParams();
  const navigate = useNavigate();

  const [editingDefault, setEditingDefault] = useState(false);
  const [picking, setPicking] = useState(false);
  const [target, setTarget] = useState<OverrideTarget | null>(null);
  const [removing, setRemoving] = useState<RuleOverride | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const canReadGlobal = usePermission(PERMISSIONS.businessRulesGlobalRead);
  const canManageGlobal = usePermission(PERMISSIONS.businessRulesGlobalManage);
  const canReadCompanies = usePermission(PERMISSIONS.companyBusinessRulesRead);
  const canManageCompanies = usePermission(PERMISSIONS.companyBusinessRulesManage);

  const rules = useBusinessRules(canReadGlobal);
  const overrides = useRuleOverrides(canReadCompanies ? ruleKey : undefined);
  const updateDefault = useUpdateRuleDefault();
  const setOverride = useSetRuleOverride();
  const removeOverride = useRemoveRuleOverride();

  const back = (
    <button
      type="button"
      onClick={() => navigate('/regras-negocio')}
      style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--fg-muted)', fontFamily: 'var(--font-sans)', fontSize: 13.5, padding: 0 }}
    >
      <StrokeIcon d={Icons.chevronL} size={16} /> Regras de Negócio
    </button>
  );

  // The route is already gated (Portal.tsx), so this is the operator who reached it by URL.
  if (!canReadGlobal) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState
            message="Você não tem permissão para ver as regras de negócio da TMR."
            hint="Peça o acesso a quem administra os papéis."
          />
        </div>
      </div>
    );
  }

  if (rules.isPending) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <Skeleton rows={4} />
      </div>
    );
  }

  if (rules.isError) {
    return (
      <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {back}
        <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />
      </div>
    );
  }

  const rule = rules.data.items.find((item) => item.key === ruleKey);
  if (!rule) {
    return (
      <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {back}
        <div className="pa-card">
          <EmptyState
            message="Regra de negócio não encontrada."
            hint="As regras vêm do código da plataforma; confira o endereço ou volte para a lista."
          />
        </div>
      </div>
    );
  }

  const listed = overrides.data?.items ?? [];

  const pick = (company: CompanyListItem) => {
    setPicking(false);
    setTarget({
      companyId: company.id,
      companyName: company.name,
      legacyLinked: company.legacyProducerId !== null,
      current: listed.find((item) => item.companyId === company.id)?.value ?? null,
    });
  };

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      {back}

      {/* The breadcrumb right above already says "Regras de Negócio"; the eyebrow places the rule instead. */}
      <PageHeader eyebrow="Plataforma" title={rule.name} desc={rule.description} />

      {saved && <Notice tone="success">{saved}</Notice>}

      <DefaultCard rule={rule} canManage={canManageGlobal} onEdit={() => setEditingDefault(true)} />

      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, color: 'var(--ink-900)' }}>
              Empresas com exceção
            </h2>
            <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
              As demais seguem o padrão da TMR ({ruleValueLabel(rule, rule.defaultValue)}).
            </span>
          </div>
          {canReadCompanies && canManageCompanies && (
            <Btn variant="secondary" size="sm" icon={Icons.plus} onClick={() => setPicking(true)}>
              Definir exceção
            </Btn>
          )}
        </div>

        {removeOverride.error && (
          <Notice tone="danger">
            {removeOverride.error instanceof ApiError
              ? removeOverride.error.detail
              : 'Não foi possível remover a exceção.'}
          </Notice>
        )}

        {!canReadCompanies ? (
          <div className="pa-card">
            <EmptyState
              message="Você não tem permissão para ver as exceções das empresas."
              hint="Peça o acesso a quem administra os papéis."
            />
          </div>
        ) : overrides.isPending ? (
          <Skeleton rows={3} />
        ) : overrides.isError ? (
          <ErrorState error={overrides.error} onRetry={() => void overrides.refetch()} />
        ) : listed.length === 0 ? (
          <div className="pa-card">
            <EmptyState
              message="Nenhuma empresa com exceção."
              hint={`Todas seguem o padrão da TMR: ${ruleValueLabel(rule, rule.defaultValue)}.`}
            />
          </div>
        ) : (
          <Table
            columns={[
              { label: 'Empresa' },
              { label: 'Valor' },
              { label: 'Definida' },
              ...(canManageCompanies ? [{ label: '', align: 'right' as const }] : []),
            ]}
          >
            {listed.map((item) => (
              <OverrideRow
                key={item.companyId}
                rule={rule}
                item={item}
                canManage={canManageCompanies}
                onEdit={() => setTarget({
                  companyId: item.companyId,
                  companyName: item.companyName,
                  legacyLinked: true,
                  current: item.value,
                })}
                onRemove={() => setRemoving(item)}
              />
            ))}
          </Table>
        )}
      </section>

      {editingDefault && (
        <RuleValueModal
          title="Alterar o padrão"
          rule={rule}
          initialValue={rule.defaultValue}
          reach={ruleReach(rule, 'default')}
          confirmLabel="Salvar padrão"
          onSave={async (value) => {
            const updated = await updateDefault.mutateAsync({ key: rule.key, value });
            setSaved(`${updated.name}: o padrão agora é ${ruleValueLabel(updated, updated.defaultValue)}.`);
          }}
          onClose={() => setEditingDefault(false)}
        />
      )}

      {picking && (
        <RecordPicker<CompanyListItem>
          eyebrow="Exceção de empresa"
          title="Para qual empresa?"
          subtitle={`A empresa escolhida deixa de seguir o padrão da TMR (${ruleValueLabel(rule, rule.defaultValue)}) nesta regra.`}
          placeholder="Buscar por nome, CNPJ ou domínio…"
          useSearch={useCompanySearch}
          getKey={(item) => item.id}
          toRow={(item) => ({
            primary: item.name,
            secondary: [
              item.cnpj ? maskCNPJ(item.cnpj) : 'Sem CNPJ',
              item.legacyProducerId === null ? 'ainda não replicada ao legado' : `produtor ${item.legacyProducerId}`,
            ].join(' · '),
          })}
          rowIcon={Icons.clinic}
          idleMessage="Busque a empresa por nome, CNPJ ou domínio."
          notFoundMessage="Nenhuma empresa encontrada."
          onSelect={pick}
          onClose={() => setPicking(false)}
        />
      )}

      {target && (
        <RuleValueModal
          title={target.current === null ? 'Definir exceção' : 'Alterar a exceção'}
          lead={`${rule.name} · ${target.companyName}`}
          rule={rule}
          initialValue={target.current ?? rule.defaultValue}
          reach={ruleReach(rule, 'override')}
          blocked={overrideIneligibility({ legacyLinked: target.legacyLinked })}
          confirmLabel="Salvar exceção"
          onSave={async (value) => {
            await setOverride.mutateAsync({ companyId: target.companyId, key: rule.key, value });
            setSaved(`${target.companyName}: ${rule.name} agora é ${ruleValueLabel(rule, value)}.`);
          }}
          onClose={() => setTarget(null)}
        />
      )}

      {removing && (
        <ConfirmDialog
          title="Remover a exceção?"
          message={`${removing.companyName} volta a seguir o padrão da TMR: ${ruleValueLabel(rule, rule.defaultValue)}.`}
          confirmLabel="Remover exceção"
          danger
          icon={Icons.alert}
          // A refusal closes the dialog and surfaces in the notice above the list, where it stays readable.
          onConfirm={() => removeOverride.mutateAsync({ companyId: removing.companyId, key: rule.key })
            .then(() => setSaved(`${removing.companyName} voltou a seguir o padrão da TMR.`))
            .catch(() => undefined)}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

/** Props for {@link DefaultCard}. */
interface DefaultCardProps {
  rule: BusinessRule;
  canManage: boolean;
  onEdit: () => void;
}

/** The TMR's default: the value in force, what the rule accepts, how far it reaches, and who set it. */
function DefaultCard({ rule, canManage, onEdit }: DefaultCardProps) {
  return (
    <div
      className="pa-card"
      // `.pa-card` is only surface — border, radius and background. Every use gives it its own padding.
      style={{
        padding: '18px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 20,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="pa-eyebrow">Padrão da TMR</div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 22, color: 'var(--ink-900)', marginTop: 6 }}>
          {ruleValueLabel(rule, rule.defaultValue)}
        </div>
        <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--fg-subtle)' }}>
          {ruleRangeLabel(rule)} · {overrideCountLabel(rule.overrideCount)} · {defaultOriginLabel(rule)}
        </div>
      </div>
      {canManage && (
        <Btn variant="secondary" size="sm" icon={Icons.pen} onClick={onEdit}>
          Alterar padrão
        </Btn>
      )}
    </div>
  );
}

/** Props for {@link OverrideRow}. */
interface OverrideRowProps {
  rule: BusinessRule;
  item: RuleOverride;
  canManage: boolean;
  onEdit: () => void;
  onRemove: () => void;
}

/** One Empresa with its own value: who it is (a link to it), the value, and who set it. */
function OverrideRow({ rule, item, canManage, onEdit, onRemove }: OverrideRowProps) {
  return (
    <tr>
      <Td>
        <Link to={`/empresas/${item.companyId}`} style={{ color: 'var(--ink-900)', fontWeight: 600, textDecoration: 'none' }}>
          {item.companyName}
        </Link>
        <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)', marginTop: 2 }}>
          {item.cnpj ? maskCNPJ(item.cnpj) : 'Sem CNPJ'} · produtor {item.legacyProducerId}
        </div>
      </Td>
      <Td>
        {/* A value reads as a value: same face as the default above it. */}
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 15 }}>
          {ruleValueLabel(rule, item.value)}
        </span>
      </Td>
      <Td style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
        {overrideOriginLabel(item.updatedAt, item.updatedByName)}
      </Td>
      {canManage && (
        <Td align="right">
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <Btn variant="secondary" size="sm" icon={Icons.pen} onClick={onEdit}>
              Alterar
            </Btn>
            <Btn variant="ghost" size="sm" icon={Icons.x} onClick={onRemove}>
              Remover
            </Btn>
          </div>
        </Td>
      )}
    </tr>
  );
}
